import { DEFAULT_LANGUAGE, detectBrowserLanguage, type LanguageCode } from "./languages.js";
import type { MessageKey } from "./keys.generated.js";

// Framework-free translation store. React components subscribe through
// useT()/useLanguage() (see useT.ts); plain-TS callers (main.tsx's global
// error handlers, api/commands.ts) call t() directly — mirrors how
// ToastProvider exposes pushToast() outside React.
//
// Catalogs live in packages/ui/locales/<code>.json — served verbatim at
// /locales/<code>.json by each app's build (see vite/localeCatalogs.ts) as
// static resources, never bundled — and are fetched on demand the first time
// a language is used. scripts/i18n-check.mjs enforces that every catalog has
// exactly the English key set, so a loaded catalog never misses a key except
// under deployment skew (handled by falling back to the key itself).

/** A catalog value is a plain template, or plural forms keyed by
 * Intl.PluralRules category ("one"/"other"/...) selected via params.count. */
type CatalogValue = string | Record<string, string>;
type Catalog = Record<string, CatalogValue>;

export type TParams = Record<string, string | number>;


const catalogs = new Map<LanguageCode, Catalog>();
const overlays = new Map<LanguageCode, Catalog>();
const listeners = new Set<() => void>();
const warnedKeys = new Set<string>();

let language: LanguageCode = DEFAULT_LANGUAGE;
let catalog: Catalog = {};
let ready = false;
/** Bumped on every visible change; useSyncExternalStore snapshot. */
let version = 0;

function notify(): void {
  version++;
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getVersion(): number {
  return version;
}

export function getLanguage(): LanguageCode {
  return language;
}

/** False only during boot, before the initial catalog has arrived. */
export function isReady(): boolean {
  return ready;
}


async function fetchCatalog(code: LanguageCode): Promise<Catalog> {
  const cached = catalogs.get(code);
  if (cached) return cached;
  const response = await fetch(`/locales/${code}.json`);
  if (!response.ok) throw new Error(`Failed to load locale ${code}: HTTP ${response.status}`);
  const data = (await response.json()) as Catalog;
  catalogs.set(code, data);
  return data;
}

function applyLanguage(code: LanguageCode, data: Catalog): void {
  language = code;
  catalog = { ...data, ...(overlays.get(code) ?? {}) };
  document.documentElement.lang = code;
  // Deliberately NOT persisted here: the user's language lives on the server
  // (users.language) so it follows them to any device. See
  // preferences/usePreferences.ts.
  notify();
}

/** The signed-in app can apply reviewed server overrides over its static catalog. */
export function setCatalogOverrides(code: LanguageCode, values: Record<string, string>): void {
  const base = catalogs.get(code);
  if (!base) return;
  const safe: Catalog = {};
  for (const [key, value] of Object.entries(values)) {
    if (typeof base[key] === "string" && typeof value === "string") safe[key] = value;
  }
  overlays.set(code, safe);
  if (language === code) applyLanguage(code, base);
}

/**
 * Switch the UI language, fetching the catalog on first use. Resolves false
 * (leaving the current language active) if the catalog can't be fetched —
 * the caller owns telling the user, since this module sits below the toast
 * layer.
 */
export async function setLanguage(code: LanguageCode): Promise<boolean> {
  try {
    const data = await fetchCatalog(code);
    applyLanguage(code, data);
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
}

let initPromise: Promise<void> | null = null;

/** Boot: browser language, else English. The user's SAVED language lives on
 * the server and can only be applied once the session has loaded, so it is
 * adopted slightly later by useServerLanguage — this only has to get the
 * pre-authentication screens (landing, sign-in) into a sensible language.
 * Idempotent (StrictMode re-runs effects). The app renders keys, not blank, if
 * even the English catalog is unreachable — hence ready flips true regardless. */
export function initI18n(): Promise<void> {
  initPromise ??= (async () => {
    const initial = detectBrowserLanguage();
    if (!(await setLanguage(initial)) && initial !== DEFAULT_LANGUAGE) {
      await setLanguage(DEFAULT_LANGUAGE);
    }
    ready = true;
    notify();
  })();
  return initPromise;
}

const pluralRules = new Map<LanguageCode, Intl.PluralRules>();

function selectPlural(forms: Record<string, string>, count: number): string | undefined {
  let rules = pluralRules.get(language);
  if (!rules) {
    try {
      rules = new Intl.PluralRules(language);
    } catch {
      rules = new Intl.PluralRules("en");
    }
    pluralRules.set(language, rules);
  }
  return forms[rules.select(count)] ?? forms.other;
}

function interpolate(template: string, params?: TParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match
  );
}

/**
 * Look up a translation. `params` fills `{name}` placeholders; `params.count`
 * additionally selects the plural form when the catalog value is a
 * plural-forms object.
 */
export function t(key: MessageKey, params?: TParams): string {
  const value = catalog[key];
  if (value === undefined) {
    if (ready && !warnedKeys.has(key)) {
      warnedKeys.add(key);
      console.warn(`Missing translation for "${key}" in "${language}"`);
    }
    return key;
  }
  if (typeof value === "string") return interpolate(value, params);
  const count = typeof params?.count === "number" ? params.count : 0;
  const form = selectPlural(value, count);
  return form === undefined ? key : interpolate(form, params);
}

/** Test-only: install a catalog synchronously so components render real
 * strings without fetch. */
export function primeI18nForTests(code: LanguageCode, data: Record<string, unknown>): void {
  catalogs.set(code, data as Catalog);
  language = code;
  catalog = data as Catalog;
  ready = true;
  notify();
}
